/**
 * Site name sorting — Airtable `Sortkey` plus leading-article handling.
 *
 * The alphabetical sort on the sites list compared `fields.Site` verbatim,
 * which filed every "The …" site under T: "The Cliff Dwellers Club", "The
 * Mart", "The Rookery" all landed between "Testa Produce" and "Thalia Hall"
 * instead of under C, M and R where a reader looks for them. 104 records on
 * Organizations start with "The ".
 *
 * Two inputs decide where a site sorts:
 *
 *   1. `Sortkey` (single-line text on Organizations) — the editorial override.
 *      Set on 129 of the 202 confirmed sites; where it differs from `Site` it
 *      is a deliberate reordering: "Bubbly + Norwich" -> "Chicago Sustainable
 *      Manufacturing Center", "Hampton Inn Michigan Avenue (Chicago Motor
 *      Club)" -> "Hampton Inn Michigan Avenue". When it is blank we fall back
 *      to `Site`, so partial coverage is fine.
 *   2. The leading article, stripped in code. Only ~10 of those 104 "The …"
 *      sites carry a Sortkey, so honouring Sortkey alone would fix a tenth of
 *      the problem. Stripping is applied to whichever string we ended up with,
 *      including an explicit Sortkey — editors who fill Sortkey in are already
 *      dropping "The" by hand, so the two rules agree rather than fight.
 *
 * The raw values need cleaning before they can be compared. Real Sortkey
 * content in the base includes an HTML entity ("Chicago Industrial Arts &amp;
 * Design Center"), embedded newlines ("Seventeenth Church of\n  Christ,
 * Scientist") and wrapping quotes ('"Industrious, Evanston"'), and `Site`
 * carries a non-breaking space ("The Chicago Theological Seminary") and
 * trailing spaces ("The Resurrection Project ").
 */

const ENTITIES: Record<string, string> = {
    amp: '&',
    nbsp: ' ',
    quot: '"',
    apos: "'",
    lt: '<',
    gt: '>',
    '#39': "'",
    '#039': "'",
};

const decodeEntities = (value: string): string =>
    value.replace(/&(#0?39|amp|nbsp|quot|apos|lt|gt);/g, (match, name: string) => ENTITIES[name] ?? match);

// Only a matched pair is stripped. A blunt "trim any quote off either end"
// would also eat the apostrophe in a name that legitimately ends in one.
const QUOTE_PAIRS: Record<string, string> = { '"': '"', "'": "'", '“': '”', '‘': '’' };

const unwrapQuotes = (value: string): string => {
    let out = value;
    while (out.length > 1 && QUOTE_PAIRS[out[0]!] === out[out.length - 1]) {
        out = out.slice(1, -1).trim();
    }
    return out;
};

const LEADING_ARTICLE = /^the\s+/i;

/** Clean one raw name into the string the collator actually compares. */
export const normalizeSortName = (raw: unknown): string => {
    // `\s` covers the non-breaking space and the embedded newlines.
    const collapsed = decodeEntities(String(raw ?? '')).replace(/\s+/g, ' ').trim();
    return unwrapQuotes(collapsed).replace(LEADING_ARTICLE, '').trim();
};

/** The sort key for a site record: `Sortkey` when set, else `Site`. */
export const siteSortKey = (site: any): string => {
    const fields = site?.fields ?? site ?? {};
    const override = typeof fields.Sortkey === 'string' ? fields.Sortkey.trim() : '';
    return normalizeSortName(override || fields.Site);
};

// `numeric` so "900 N. Michigan" reads as nine hundred rather than 9-0-0, and
// `base` so case and accents ("Alliance Française") don't split the ordering.
const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

/**
 * Comparator for the sites list. Falls back to the displayed name when two
 * records share a key — "The Mill" and "Mill" both normalize to "mill", and
 * without the tiebreak their order would depend on the input array.
 */
export const compareSitesByName = (a: any, b: any): number =>
    collator.compare(siteSortKey(a), siteSortKey(b)) ||
    collator.compare(String(a?.fields?.Site ?? ''), String(b?.fields?.Site ?? ''));
